import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, Phone, MessageSquare, Calendar, Users, MapPin } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface Guide {
  id: number;
  name: string;
  image: string;
  rating: number;
  specialties: string[];
}

interface CityTourBookingFormProps {
  isOpen: boolean;
  onClose: () => void;
  tourTitle: string;
  tourPrice: number;
  tourDuration: string;
}

const guides: Guide[] = [
  {
    id: 1,
    name: 'David Mugisha',
    image: 'https://imgur.com/ERlskFe.jpg',
    rating: 4.9,
    specialties: ['City Tours', 'Cultural Tours']
  },
  {
    id: 2,
    name: 'Sarah Namukasa',
    image: 'https://imgur.com/lCTURxX.jpg',
    rating: 4.8,
    specialties: ['Walking Tours', 'Food Tours']
  },
  {
    id: 3,
    name: 'Ankunda Prima',
    image: 'https://imgur.com/ssIyPUG.jpg',
    rating: 4.7,
    specialties: ['Adventure Tours', 'City Exploration']
  }
];

const CityTourBookingForm: React.FC<CityTourBookingFormProps> = ({ 
  isOpen, 
  onClose, 
  tourTitle,
  tourPrice,
  tourDuration
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedGuide, setSelectedGuide] = useState<number | null>(null);
  const [participants, setParticipants] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const selectedGuideInfo = guides.find(g => g.id === selectedGuide);
      
      const formBody = {
        "form-name": "city-tour-booking-form",
        ...formData,
        date: selectedDate?.toLocaleDateString() || '',
        participants: participants.toString(),
        tour: tourTitle,
        duration: tourDuration,
        totalPrice: (tourPrice * participants).toString(),
        guide: selectedGuideInfo?.name || 'Any available guide'
      };

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        onClose();
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your booking. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-[280px]"
      >
        <div className="p-2 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xs font-medium text-gray-900">Book {tourTitle}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-3 w-3" />
          </button>
        </div>

        <form 
          name="city-tour-booking-form"
          method="POST"
          data-netlify="true"
          onSubmit={handleSubmit}
          className="p-2"
        >
          <input type="hidden" name="form-name" value="city-tour-booking-form" />
          <input type="hidden" name="tour" value={tourTitle} />
          <input type="hidden" name="duration" value={tourDuration} />
          <input type="hidden" name="totalPrice" value={(tourPrice * participants).toString()} />

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-medium text-gray-700">Date *</label>
                <div className="relative">
                  <Calendar className="absolute left-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    minDate={new Date()}
                    className="w-full pl-6 pr-1 py-1 text-xs border border-gray-300 rounded focus:ring-ta-green focus:border-ta-green"
                    placeholderText="Select"
                    required
                    name="date"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-700">Guests *</label>
                <div className="flex">
                  <button 
                    type="button"
                    onClick={() => setParticipants(prev => Math.max(1, prev - 1))}
                    className="px-2 py-1 border border-gray-300 rounded-l bg-gray-50 text-xs"
                  >-</button>
                  <input 
                    type="number" 
                    value={participants}
                    name="participants"
                    onChange={(e) => setParticipants(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-8 text-center border-y border-gray-300 py-1 text-xs"
                    min="1"
                  />
                  <button 
                    type="button"
                    onClick={() => setParticipants(prev => prev + 1)}
                    className="px-2 py-1 border border-gray-300 rounded-r bg-gray-50 text-xs"
                  >+</button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700">Guide (Optional)</label>
              <div className="grid grid-cols-3 gap-1">
                {guides.map(guide => (
                  <div 
                    key={guide.id}
                    className={`p-1 border rounded cursor-pointer transition-colors ${
                      selectedGuide === guide.id 
                        ? 'border-ta-green bg-ta-green/5' 
                        : 'border-gray-200 hover:border-ta-green/50'
                    }`}
                    onClick={() => setSelectedGuide(guide.id)}
                  >
                    <div className="flex flex-col items-center">
                      <img 
                        src={guide.image} 
                        alt={guide.name} 
                        className="h-6 w-6 rounded-full object-cover"
                      />
                      <p className="text-[8px] text-center mt-0.5">{guide.name}</p>
                      <div className="flex items-center mt-0.5">
                        <svg className="h-2 w-2 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-[8px] ml-0.5">{guide.rating}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-ta-green focus:border-ta-green"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-700">Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-6 pr-1 py-1 text-xs border border-gray-300 rounded focus:ring-ta-green focus:border-ta-green"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700">Email *</label>
              <div className="relative">
                <Mail className="absolute left-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-6 pr-1 py-1 text-xs border border-gray-300 rounded focus:ring-ta-green focus:border-ta-green"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700">Notes</label>
              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-ta-green focus:border-ta-green"
                placeholder="Any special requests?"
              />
            </div>

            <div className="bg-gray-50 p-1.5 rounded text-[10px]">
              <div className="flex justify-between mb-0.5">
                <span className="text-gray-600">Price × {participants}</span>
                <span>${tourPrice * participants}</span>
              </div>
              <div className="border-t border-gray-200 mt-0.5 pt-0.5 flex justify-between font-medium">
                <span>Total</span>
                <span>${tourPrice * participants}</span>
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-1 bg-ta-green text-white text-xs rounded hover:bg-ta-green-dark transition-colors"
            >
              Complete Booking
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CityTourBookingForm;